/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1682640000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Fri Apr 28 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "ams",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to NCR Advanced Marketing Solution",
          "depth": 0,
          "id": "connect-to-ncr-advanced-marketing-solution"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing customer groups",
          "depth": 1,
          "id": "syncing-customer-groups"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      p: "p",
      a: "a",
      ul: "ul",
      li: "li",
      h3: "h3",
      code: "code",
      h4: "h4",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Customer group"
              })
            }), _jsx(_components.td, {
              children: "Sync customer groups by external ID to NCR Advanced Marketing Solution"
            }), _jsx(_components.td, {
              children: "Insert, All"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-ncr-advanced-marketing-solution",
        children: "Connect to NCR Advanced Marketing Solution"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "NCR Advanced Marketing Solution"
        }), " (AMS) and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "NCR Advanced Marketing Solution"
        }), " by entering the following fields into Hightouch:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Host"
          }), ": The hostname or IP address of your NCR AMS instance"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Username"
          }), ": This can be your personal NCR AMS login or a dedicated user for Hightouch"]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) ", _jsx(_components.strong, {
            children: "Password"
          }), ": The password for the specified user"]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) ", _jsx(_components.strong, {
            children: "Private Key"
          }), ": An SSH private key"]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) ", _jsx(_components.strong, {
            children: "Private Key Passphrase"
          }), ": The passphrase associated with the private key"]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your NCR AMS destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the NCR AMS destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-customer-groups",
        children: "Syncing customer groups"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch syncs customer groups to NCR AMS as ", _jsx(_components.code, {
          children: ".cgi"
        }), " files.\nHightouch automatically batches your query results into up to two million rows per file."]
      }), "\n", _jsxs(_components.p, {
        children: ["Each ", _jsx(_components.code, {
          children: ".cgi"
        }), " file contains ", _jsx(_components.code, {
          children: "ID"
        }), ",", _jsx(_components.code, {
          children: "Name"
        }), ", and ", _jsx(_components.code, {
          children: "BulkData"
        }), " XML elements.\nThe ", _jsx(_components.code, {
          children: "BulkData"
        }), " element contains the external IDs of the customer groups you are syncing."]
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to the ", _jsxs(_components.a, {
          href: "#example-file",
          children: ["example ", _jsx(_components.code, {
            children: ".cgi"
          }), " file"]
        }), " for details."]
      }), "\n", _jsx(_components.h4, {
        id: "file-path",
        children: "File path"
      }), "\n", _jsxs(_components.p, {
        children: ["The file path defines where Hightouch should send the ", _jsx(_components.code, {
          children: ".cgi"
        }), " files.\nThe file naming convention is ", _jsx(_components.code, {
          children: "<<Test/Control Group name>>_<<CellName or AMSID_UPCID>>_<<File number>>.cgi"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch automatically adds the file number so you only need to provide the ", _jsx(_components.code, {
          children: "<<Test/Control Group name>>_<<CellName or AMSID_UPCID>>.cgi"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["For example, suppose you input ", _jsx(_components.code, {
          children: "TestGroup_AMSID_73237_UPC_53395004106.cgi"
        }), ", the first two million rows of data would appear in a ", _jsx(_components.code, {
          children: ".cgi"
        }), " file with the name ", _jsx(_components.code, {
          children: "TestGroup_AMSID_73237_UPC_53395004106_01"
        }), ".\nThe second two million rows would appear in a file with the name ", _jsx(_components.code, {
          children: "TestGroup_AMSID_73237_UPC_53395004106_02"
        }), ", etc."]
      }), "\n", _jsx(_components.h4, {
        id: "batch-id",
        children: "Batch ID"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch uses your input in the ", _jsx(_components.strong, {
          children: "Batch ID"
        }), " for the ", _jsx(_components.code, {
          children: "Name"
        }), " and ", _jsx(_components.code, {
          children: "ID"
        }), " elements in the ", _jsx(_components.code, {
          children: ".cgi"
        }), " file and automatically suffixes a ", _jsx(_components.code, {
          children: "_<<File number>>"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["For example, suppose you input ", _jsx(_components.code, {
          children: "37293_US"
        }), " as the batch ID, the first two million rows would use ", _jsx(_components.code, {
          children: "37293_US_01"
        }), " for both ", _jsx(_components.code, {
          children: "Name"
        }), " and ", _jsx(_components.code, {
          children: "ID"
        }), " elements.\nThe second two million rows would use ", _jsx(_components.code, {
          children: "37293_US_02"
        }), " for ", _jsx(_components.code, {
          children: "Name"
        }), " and ", _jsx(_components.code, {
          children: "ID"
        }), " elements, etc."]
      }), "\n", _jsx(_components.h4, {
        id: "external-id-column",
        children: "External ID column"
      }), "\n", _jsxs(_components.p, {
        children: ["Use this field to specify which column from your model contains the external IDs to sync.\nIn the ", _jsx(_components.code, {
          children: ".cgi"
        }), " file, these values have the suffix ", _jsx(_components.code, {
          children: ",0\\n"
        }), " (comma, zero digit, and new line) and are sent as the ", _jsx(_components.code, {
          children: "BulkData"
        }), " element."]
      }), "\n", _jsx(_components.h4, {
        id: "example-file",
        children: "Example file"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch syncs ", _jsx(_components.code, {
          children: ".cgi"
        }), " files with contents like this:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-xml",
          children: [_jsxs(_components.span, {
            className: "hljs-meta",
            children: ["<?xml version=", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"1.0\""
            }), " encoding=", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"Windows-1252\""
            }), " standalone=", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"no\""
            }), "?>"]
          }), "\n", _jsxs(_components.span, {
            className: "hljs-tag",
            children: ["<", _jsx(_components.span, {
              className: "hljs-name",
              children: "CustomerGroupUpdate"
            }), " ", _jsx(_components.span, {
              className: "hljs-attr",
              children: "xmlns:xsi"
            }), "=", _jsx(_components.span, {
              className: "hljs-string",
              children: "\"http://www.w3.org/2001/XMLSchema-instance\""
            }), ">"]
          }), "\n ", _jsxs(_components.span, {
            className: "hljs-tag",
            children: ["<", _jsx(_components.span, {
              className: "hljs-name",
              children: "ImportByExternalID"
            }), ">"]
          }), "\n  ", _jsxs(_components.span, {
            className: "hljs-tag",
            children: ["<", _jsx(_components.span, {
              className: "hljs-name",
              children: "ID"
            }), ">"]
          }), "37293_US_01", _jsxs(_components.span, {
            className: "hljs-tag",
            children: ["</", _jsx(_components.span, {
              className: "hljs-name",
              children: "ID"
            }), ">"]
          }), "\n  ", _jsxs(_components.span, {
            className: "hljs-tag",
            children: ["<", _jsx(_components.span, {
              className: "hljs-name",
              children: "Name"
            }), ">"]
          }), "37293_US_01", _jsxs(_components.span, {
            className: "hljs-tag",
            children: ["</", _jsx(_components.span, {
              className: "hljs-name",
              children: "Name"
            }), ">"]
          }), "\n  ", _jsxs(_components.span, {
            className: "hljs-tag",
            children: ["<", _jsx(_components.span, {
              className: "hljs-name",
              children: "BulkData"
            }), ">"]
          }), "\n    405247281386905506,0\n    213550969606979806,0\n    497647364788236008,0\n    914660244431276315,0\n    637947282525555405,0\n    525462241744844516,0\n    494763443801427416,0\n  ", _jsxs(_components.span, {
            className: "hljs-tag",
            children: ["</", _jsx(_components.span, {
              className: "hljs-name",
              children: "BulkData"
            }), ">"]
          }), "\n  ", _jsxs(_components.span, {
            className: "hljs-tag",
            children: ["<", _jsx(_components.span, {
              className: "hljs-name",
              children: "Operation"
            }), ">"]
          }), "augment", _jsxs(_components.span, {
            className: "hljs-tag",
            children: ["</", _jsx(_components.span, {
              className: "hljs-name",
              children: "Operation"
            }), ">"]
          }), "\n ", _jsxs(_components.span, {
            className: "hljs-tag",
            children: ["</", _jsx(_components.span, {
              className: "hljs-name",
              children: "ImportByExternalID"
            }), ">"]
          }), "\n", _jsxs(_components.span, {
            className: "hljs-tag",
            children: ["</", _jsx(_components.span, {
              className: "hljs-name",
              children: "CustomerGroupUpdate"
            }), ">"]
          }), "\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
